import { Component, HostListener, OnInit } from '@angular/core'
import { FormControl, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatTableDataSource } from '@angular/material/table'
import { ActivatedRoute, Router } from '@angular/router'
import moment, { Moment } from 'moment'
import { firstValueFrom, forkJoin } from 'rxjs'
import { ClientDto } from 'src/app/models/client/client-dto'
import { AssessmentDto } from 'src/app/models/flip/assessment-dto'
import { AssessmentGroupDto } from 'src/app/models/flip/assessment-group-dto'
import { AssessmentItemDto } from 'src/app/models/flip/assessment-item-dto'
import { DeleteAssessmentRequestDto } from 'src/app/models/flip/delete-assessment-request'
import { EditAssessmentDateRequestDto } from 'src/app/models/flip/edit-assessment-date-request'
import { FlipHistoryDto } from 'src/app/models/flip/flip-history-dto'
import { FlipHistoryTableDto } from 'src/app/models/flip/flip-history-table-dto'
import { FlipDeleteConfirmModalComponent } from '../../complex/modals/flip-delete-confirm-modal/flip-delete-confirm-modal.component'
import { Filter } from 'src/app/models/power-bi/filter'
import { FlipHistoryTableColumnDefinitions } from 'src/app/models/table-defs/flip-history-table-def'
import { TableColumnDefinition } from 'src/app/models/table/table-column-definition'
import { TableLinkColumnDTO } from 'src/app/models/table/table-link-column-dto'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientService } from 'src/app/services/client/client.service'
import { FlipService } from 'src/app/services/flip/flip.service'
import { PreviousRouteService } from 'src/app/services/previous-route/previous-route.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'
import { ClientInformationAttribute } from 'src/app/models/client-information/ClientInformationAttribute'

import {
  SubmitEntryModalComponent,
  SubmitEntryModalResponse,
} from '../../complex/modals/submit-entry-modal/submit-entry-modal.component'
import { CommonService } from '../../../services/common/common.service'
import { FeaturesService } from '../../../services/features/features.service'
import { TranslateService } from '@ngx-translate/core'

export class Group {
  header: string
  subItems: Item[] = []
}

export class Item {
  number: string
  content: string
  score: string
}

@Component({
  selector: 'app-flip-history',
  templateUrl: './flip-history.component.html',
  styleUrls: ['./flip-history.component.scss'],
  standalone: false,
})
export class FlipHistoryComponent implements OnInit {
  _assessment: AssessmentDto
  _age: string
  _client: ClientDto
  _clientId: string
  _collapsibleItems: Group[] = new Array<Group>()
  _dateFormat: string = 'Do MMMM, YYYY'
  _dob: Date
  _flipFilters: Filter[]
  _fallbackUrl: string = '/dashboard'
  _fullPageReportLink: string = ''
  _createFlipReportLink: string = ''
  _history: FlipHistoryDto[] = new Array<FlipHistoryDto>()
  _lastAssessmentDate: string
  _lastAssessmentDateAgo: string
  _lastAssessmentDateUnavailable: boolean = false
  _tableColumnDefinitions: TableColumnDefinition[] =
    FlipHistoryTableColumnDefinitions
  _tableColumnsToDisplay: string[] = [
    'assessmentDate',
    'age',
    'score',
    'scoreRange',
    'dataEnteredBy',
    'notes',
    'view',
  ]
  _tableData: MatTableDataSource<FlipHistoryTableDto> =
    new MatTableDataSource<FlipHistoryTableDto>()
  _title: string
  _editMode: boolean = false
  _editDateControl: FormControl = new FormControl<Moment | null>(null, [Validators.required])
  _mostRecentInstanceId: string | null = null
  _todaysDateFormatted: string = moment().format(this._dateFormat)
  _assessmentPanelSize: string
  _graphPanelSize: string
  _overviewPanelSize: string

  //Client Attributes
  _clientInformationAttributes: ClientInformationAttribute[] =
    this.commonService.createEmptyAttributeList()
  _clientInformationTitle = 'Client Information'
  _clientInformationDisplayText = ''
  _clientInformationUrl = ''
  _clientInFocus: string = ''
  _clientEditEnabled: boolean = false //Perform check based on Feature Flag
  _totalReviews = 0
  _hasAccess: any = {
    slatHasAccess: false,
    flipHasAccess: false,
    reddiHasAccess: false,
  }

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly bannerService: BannerService,
    private readonly clientService: ClientService,
    private readonly dialog: MatDialog,
    private readonly flipService: FlipService,
    protected previousRouteService: PreviousRouteService,
    private readonly router: Router,
    public spinnerService: SpinnerService,
    private readonly commonService: CommonService,
    public featureService: FeaturesService,
    private translateService: TranslateService,
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize() {
    this._assessmentPanelSize = window.innerWidth <= 1600 ? 'x-medium' : 'small'
    this._graphPanelSize = window.innerWidth <= 1600 ? 'x-large' : 'large'
    this._overviewPanelSize = window.innerWidth <= 1600 ? 'x-medium' : 'x-large'
  }

  ngOnInit(): void {
    this.spinnerService.show()
    this.onResize()

    this._clientId = this.activatedRoute.snapshot.paramMap.get('clientId')
    if (!this.commonService.isValidUUID(this._clientId)) {
      this.bannerService.showBanner(`Invalid client requested.`, 'error', 3000)
      this.previousRouteService.goToPreviousUrl(this._fallbackUrl)
    }

    this._fullPageReportLink = `/flip/graph/${this._clientId}`
    this._createFlipReportLink = `/flip/flip-report-form/${this._clientId}`
    this._flipFilters = [
      {
        attribute: 'Client/contactid',
        operation: 'eq',
        value: this._clientId,
      },
    ]

    this.fetchData()
  }

  fetchData() {
    let requests = {
      clientDetails: this.clientService.Details(this._clientId),
      getAssessment: this.flipService.GetLastAssessment(this._clientId),
      history: this.flipService.History(this._clientId),
      features: this.featureService.GetFeatures(),
    }

    forkJoin(requests).subscribe({
      next: ({ clientDetails, getAssessment, history, features }) => {
        this._clientEditEnabled = this.commonService.getFeatureValue(
          features,
          'clientEdit',
        )
        this._assessment = getAssessment
        this.setClientInformation(clientDetails)

        if (
          this._assessment.lastAssessmentDate === null ||
          this._assessment.lastAssessmentDate === undefined
        ) {
          this._lastAssessmentDateUnavailable = true
        }

        this._lastAssessmentDate = moment(
          this._assessment.lastAssessmentDate,
        ).format(this._dateFormat)
        this._lastAssessmentDateAgo = this.CalculateTimeSinceLastAssessmentDate(
          new Date(this._assessment.lastAssessmentDate),
        )

        this._collapsibleItems = this.MapAssessmentToCollapsible(
          this._assessment,
        )

        this._client = clientDetails
        this._age = this._client.age
        this._dob = this._client.dateOfBirth
        this._title = this._client.preferredFullName

        this._history = history
        this._tableData = this.MapHistoryToTable(
          this._assessment.clientId,
          this._history,
        )
        this._mostRecentInstanceId = this._tableData.data[0]?.instanceId ?? null

        //tools has access check
        this.checkToolsAccess()
        this.retrieveIndicators(clientDetails)
        this.spinnerService.hide()
      },
      error: () => {
        this.bannerService.showBanner(
          `Unable to retrieve assessment details.`,
          'error',
          3000,
        )
        this.previousRouteService.goToPreviousUrl(this._fallbackUrl)
      },
    })
  }

  routerLinkClicked() {
    this.previousRouteService.clearPreviousUrl()
    this.previousRouteService.goToPreviousUrl(
      '/client-dashboard/' + this._clientId,
    )
  }

  CalculateTimeSinceLastAssessmentDate(date: Date): string {
    const timeDifference = new Date().getTime() - date.getTime()
    const dayDifference = Math.floor(timeDifference / (1000 * 3600 * 24))

    if (dayDifference < 7) {
      return `${dayDifference} days`
    } else {
      return `${Math.floor(dayDifference / 7)} weeks`
    }
  }

  MapAssessmentToCollapsible(assessment: AssessmentDto): Group[] {
    const result: Group[] = []

    if (assessment.groups !== null) {
      for (const element of assessment.groups) {
        result.push(this.MapAssessmentGroupToCollapsibleGroup(element))
      }
    }

    return result
  }

  MapAssessmentGroupToCollapsibleGroup(group: AssessmentGroupDto): Group {
    const result: Group = new Group()

    result.header = group.name ?? ''

    if (group.items !== null) {
      for (const element of group.items) {
        result.subItems.push(this.MapAssessmentItemToCollapsibleItem(element))
      }
    }

    return result
  }

  MapAssessmentItemToCollapsibleItem(item: AssessmentItemDto): Item {
    return {
      number: item.itemNumber,
      content: item.name,
      score: this.GetScoreText(item.score),
    }
  }

  MapHistoryToTable(
    clientId: string,
    history: FlipHistoryDto[],
  ): MatTableDataSource<FlipHistoryTableDto> {
    const tableData: FlipHistoryTableDto[] = new Array<FlipHistoryTableDto>()

    for (const element of history) {
      let source = element

      let target = new FlipHistoryTableDto()
      target.date = source.assessmentDate
      target.assessmentDate = moment(source.assessmentDate).format(
        this._dateFormat,
      )
      target.dataEnteredBy = source.ownerName
      target.notes = source.notes
      target.scoreRange = this.translateService.instant(source.scoreRange)
      target.age = source.ageInMonthsAtAssessment
      target.view = new TableLinkColumnDTO()
      target.view.linkText = 'View details'
      target.view.linkUrl = `/flip/instance/${clientId}/${source.instanceId}`

      target.score = source.score.toString()
      target.instanceId = source.instanceId

      tableData.push(target)
    }

    tableData.sort((a, b) => {
      if (a.date > b.date) {
        return -1
      } else if (a.date < b.date) {
        return 1
      }

      return 0
    })

    return new MatTableDataSource<FlipHistoryTableDto>(tableData)
  }

  navigateTo(target: string): void {
    this.commonService.navigateTo(target, this._clientId)
  }

  NavigateToFlipAssessment() {
    this.router.navigate([`/flip-assessment/${this._clientId}`])
  }

  async SubmitExistingAssessment() {
    const dr = this.dialog.open(SubmitEntryModalComponent, {
      width: '704px',
      height: '414px',
      panelClass: 'are-you-sure-panel',
    })

    let response: SubmitEntryModalResponse = await firstValueFrom(
      dr.afterClosed(),
    )

    if (response.save) {
      this.spinnerService.show()

      // Remove time component to match main flip assessment page logic
      this._assessment.assessmentDate = new Date()
      this._assessment.assessmentDate.setHours(0, 0, 0, 0)

      this._assessment.finishingSession = true
      this._assessment.notes = response.value

      // Set to null to ensure recalculated properly by API
      this._assessment.score = null
      this._assessment.ageInDaysAtAssessment = null
      this._assessment.ageInMonthsAtAssessment = null

      //Submit
      this.flipService.Submit(this._assessment).subscribe({
        next: () => {
          this.bannerService.showBanner(
            'Successfully submitted FLI-P Assessment',
            'success',
            3000,
          )

          this.router.navigate([`/client-dashboard/${this._clientId}`])
        },
        error: () => {
          this.bannerService.showBanner(
            'Error submitting FLI-P Assessment',
            'error',
            3000,
          )
          this.spinnerService.hide()
        },
      })
    }
  }

  openEditMode() {
    const mostRecent = this._tableData.data[0]
    this._editDateControl.setValue(moment(mostRecent.date))
    this._editMode = true
  }

  cancelEdit() {
    this._editMode = false
    this._editDateControl.reset()
  }

  saveEdit() {
    if (!this._editDateControl.valid || !this._editDateControl.value) return
    this.spinnerService.show()
    this.flipService.EditAssessmentDate(
      new EditAssessmentDateRequestDto({
        instanceId: this._mostRecentInstanceId,
        newAssessmentDate: (this._editDateControl.value as Moment).toDate(),
      })
    ).subscribe({
      next: () => {
        this._editMode = false
        this.bannerService.showBanner(
          this.translateService.instant('FLIP.EDIT_DATE.SUCCESS'),
          'success',
          3000,
        )
        this.fetchData()
      },
      error: (err) => {
        this.bannerService.showBanner(
          err?.error ?? this.translateService.instant('FLIP.EDIT_DATE.ERROR'),
          'error',
          3000,
        )
        this.spinnerService.hide()
      },
    })
  }

  async deleteAssessment() {
    const dr = this.dialog.open(FlipDeleteConfirmModalComponent, {
      width: '605px',
      height: '414px',
      panelClass: 'are-you-sure-panel',
    })
    const confirmed: boolean = await firstValueFrom(dr.afterClosed())
    if (!confirmed) return

    this.spinnerService.show()
    this.flipService.DeleteAssessment(
      new DeleteAssessmentRequestDto({ instanceId: this._mostRecentInstanceId })
    ).subscribe({
      next: () => {
        this.bannerService.showBanner(
          this.translateService.instant('FLIP.DELETE.SUCCESS'),
          'success',
          3000,
        )
        this.fetchData()
      },
      error: (err) => {
        this.bannerService.showBanner(
          err?.error ?? this.translateService.instant('FLIP.DELETE.ERROR'),
          'error',
          3000,
        )
        this.spinnerService.hide()
      },
    })
  }

  GetScoreText(score: boolean | null): string {
    switch (score) {
      case true:
        return 'Mostly'
      case false:
        return 'Rarely'
      default:
        return ''
    }
  }

  setClientInformation(client: ClientDto) {
    this._clientInFocus = client.contactId
    this._clientInformationDisplayText = this._clientEditEnabled
      ? 'Edit profile'
      : ''
    this._clientInformationUrl = this._clientEditEnabled
      ? '/edit-client/' + client.contactId
      : ''

    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Age',
      client.age,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'DOB',
      moment(client.dateOfBirth).format('Do MMMM, YYYY'),
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Gender',
      client.genderCodeName,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Case Manager',
      client.clientCaseManagerName,
    )
  }

  checkToolsAccess() {
    this._hasAccess.flipHasAccess = this.commonService.clientHasAccessToFlip(
      this._client,
    )
    this._hasAccess.reddiHasAccess = this.commonService.clientHasAccessToReddi(
      this._client,
    )
    this._hasAccess.slatHasAccess = this.commonService.clientHasAccessToSlat(
      this._client,
    )
  }

  retrieveIndicators(client: ClientDto) {
    this._totalReviews = this.commonService.returnReviewIndicator(client)
  }

  previousRoute() {
    const prevUrl = this.previousRouteService.getPreviousUrl()
    if (prevUrl) {
      this.previousRouteService.goToPreviousUrl(prevUrl)
    }
  }
}
